import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarCheck,
  Clock,
  FileText,
  Gift,
  LogIn,
  Sparkles,
  User,
  Wallet,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useMobileMode from "@/hooks/useMobileMode";
import { MobilePage, GlassCard, PrettyActionCard, PrettyStatCard } from "@/components/mobile/MobilePrimitives";
import { PageHeader, Surface } from "@/components/app/Primitives";
import AnnouncementBoard from "@/components/app/AnnouncementBoard";

const php = (n) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(Number(n || 0));

function PrettyMobileDashboard() {
  const nav = useNavigate();
  const { user, activeCompanyId, companies } = useAuth();
  const [data, setData] = useState({
    attendance: null,
    pendingLeaves: 0,
    pendingOt: 0,
    payslips: 0,
    latestNet: null,
  });

  const company = companies?.find((c) => c.id === activeCompanyId);
  const firstName = user?.first_name || user?.name?.split(" ")?.[0] || "Employee";
  const role = user?.role || user?.position || "Team Member";

  useEffect(() => {
    let mounted = true;

    async function load() {
      const next = { attendance: null, pendingLeaves: 0, pendingOt: 0, payslips: 0, latestNet: null };
      // Nothing to load: this account has no employee record behind it.
      if (!(user?.employee_id || user?.employeeId)) { if (mounted) setData(next); return; }

      try {
        const status = await api.get("/timekeeping/clock-status", {
          params: user?.employee_id ? { employee_id: user.employee_id } : {},
        });
        next.attendance = status.data?.attendance || null;
      } catch {}

      try {
        const leaves = await api.get("/me/leaves");
        next.pendingLeaves = (leaves.data?.items || []).filter((x) => ["pending", "submitted"].includes(x.status)).length;
      } catch {}

      try {
        const ot = await api.get("/me/overtime");
        next.pendingOt = (ot.data?.items || []).filter((x) => ["pending", "submitted"].includes(x.status)).length;
      } catch {}

      try {
        const ps = await api.get("/me/payslips");
        const rows = ps.data?.items || [];
        next.payslips = rows.length;
        next.latestNet = rows[0]?.net_pay ?? null;
      } catch {}

      if (mounted) setData(next);
    }

    load();
    return () => {
      mounted = false;
    };
  }, [user?.employee_id, activeCompanyId]);

  const clockedIn = !!data.attendance?.time_in && !data.attendance?.time_out;
  const statusText = clockedIn ? "Clocked In" : "Not Clocked In";
  const statusSub = clockedIn ? "Remember to clock out later" : "Tap Clock In to record";

  return (
    <MobilePage>
      <div className="relative overflow-hidden rounded-b-[36px] bg-gradient-to-br from-blue-50 via-white to-cyan-50 px-1 pb-2">
        <div className="absolute right-4 top-2 h-20 w-20 rounded-full bg-yellow-200/50 blur-2xl" />
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center rounded-[24px] border-4 border-white bg-gradient-to-br from-blue-600 to-cyan-500 text-xl font-black text-white shadow-xl">
              {String(firstName).slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-500">Good morning,</div>
              <div className="text-3xl font-black leading-none text-slate-950">{firstName} 👋</div>
              <div className="mt-1 text-xs text-slate-500">{role} · {company?.name || "Company"}</div>
            </div>
          </div>

          <button onClick={() => nav("/me/time")} aria-label="My requests"
            className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-md">
            <Bell className="h-5 w-5" />
            {(data.pendingLeaves + data.pendingOt) > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black text-white">
                {data.pendingLeaves + data.pendingOt}
              </span>
            )}
          </button>
        </div>
      </div>

      <GlassCard className="relative overflow-hidden bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 p-5 text-white">
        <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/20 blur-2xl" />
        <div className="relative flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-black">You’re all set for today! 💪</div>
            <div className="mt-1 text-sm text-white/85">Let’s make it productive.</div>
          </div>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-white/20">
            <Sparkles className="h-8 w-8" />
          </div>
        </div>
        <button
          onClick={() => nav("/me/time")}
          className="relative mt-4 rounded-2xl bg-white px-4 py-3 text-sm font-black text-blue-700 shadow-lg"
        >
          View Schedule ›
        </button>
      </GlassCard>

      <AnnouncementBoard />

      <div className="space-y-3">
        <PrettyActionCard
          title="Clock In / Out"
          subtitle="GPS-verified attendance"
          badge="Secure · Accurate · Verified"
          icon={LogIn}
          imageIcon={CalendarCheck}
          gradient="from-teal-500 to-cyan-500"
          badgeClass="bg-emerald-50 text-emerald-700 border-emerald-100"
          onClick={() => nav("/me/clock-in")}
        />
        <PrettyActionCard
          title="File Leave / OT"
          subtitle="Request time off or overtime"
          badge="Easy · Fast · Paperless"
          icon={CalendarCheck}
          imageIcon={FileText}
          gradient="from-violet-500 to-purple-500"
          badgeClass="bg-violet-50 text-violet-700 border-violet-100"
          onClick={() => nav("/me/time")}
        />
        <PrettyActionCard
          title="My Payslips"
          subtitle="View and download payslips"
          badge="Secure · Quick · Anytime"
          icon={Wallet}
          imageIcon={FileText}
          gradient="from-blue-500 to-cyan-500"
          badgeClass="bg-blue-50 text-blue-700 border-blue-100"
          onClick={() => nav("/me/payslips")}
        />
        <PrettyActionCard
          title="Exchange My Reward Points"
          subtitle="Trade points for leave, GCs & merch"
          badge="Earn · Redeem · Enjoy"
          icon={Gift}
          imageIcon={Sparkles}
          gradient="from-pink-500 to-rose-500"
          badgeClass="bg-pink-50 text-pink-700 border-pink-100"
          onClick={() => nav("/rewards/exchange")}
        />
        <PrettyActionCard
          title="My Profile"
          subtitle="View and update your profile"
          badge="Personal · Up to date"
          icon={User}
          imageIcon={User}
          gradient="from-orange-500 to-amber-500"
          badgeClass="bg-orange-50 text-orange-700 border-orange-100"
          onClick={() => nav("/me/profile")}
        />
      </div>

      <GlassCard className="flex items-center gap-3 p-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
          <Clock className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm font-black">Make sure to clock in and out on time.</div>
          <div className="text-xs text-slate-500">Thank you for your hard work! 💙</div>
        </div>
      </GlassCard>
    </MobilePage>
  );
}

function DesktopDashboard() {
  const nav = useNavigate();
  const { user, companies, activeCompanyId } = useAuth();
  const company = companies?.find((c) => c.id === activeCompanyId);
  const [data, setData] = useState({ attendance: null, pendingLeaves: 0, pendingOt: 0, payslips: 0, latestNet: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const next = { attendance: null, pendingLeaves: 0, pendingOt: 0, payslips: 0, latestNet: null };
      // Nothing to load: this account has no employee record behind it.
      if (!(user?.employee_id || user?.employeeId)) { if (mounted) setData(next); return; }
      try { const s = await api.get("/timekeeping/clock-status", { params: user?.employee_id ? { employee_id: user.employee_id } : {} }); next.attendance = s.data?.attendance || null; } catch {}
      try { const l = await api.get("/me/leaves"); next.pendingLeaves = (l.data?.items || []).filter((x) => ["pending", "submitted"].includes(x.status)).length; } catch {}
      try { const o = await api.get("/me/overtime"); next.pendingOt = (o.data?.items || []).filter((x) => ["pending", "submitted"].includes(x.status)).length; } catch {}
      try { const p = await api.get("/me/payslips"); const rows = p.data?.items || []; next.payslips = rows.length; next.latestNet = rows[0]?.net_pay ?? null; } catch {}
      if (mounted) setData(next);
    })();
    return () => { mounted = false; };
  }, [user?.employee_id, activeCompanyId]);

  const clockedIn = !!data.attendance?.time_in && !data.attendance?.time_out;
  const stats = [
    { label: "Today", value: clockedIn ? "Clocked In" : "Not Clocked In", sub: clockedIn ? "Remember to clock out" : "Go to Clock In / Out", to: "/me/clock-in", icon: Clock },
    { label: "Pending Leaves", value: data.pendingLeaves, sub: "View My Leave & OT", to: "/me/time", icon: FileText },
    { label: "Pending OT", value: data.pendingOt, sub: "View My Leave & OT", to: "/me/time", icon: Clock },
    { label: "Payslips", value: data.payslips, sub: data.latestNet ? `${php(data.latestNet)} latest` : "View My Payslips", to: "/me/payslips", icon: Wallet },
  ];
  const actions = [
    { title: "Clock In / Out", subtitle: "GPS-verified attendance", to: "/me/clock-in", icon: LogIn },
    { title: "File Leave / OT", subtitle: "Request time off or overtime", to: "/me/time", icon: CalendarCheck },
    { title: "Time Adjustment", subtitle: "Fix a missed or wrong clock in/out", to: "/me/time?tab=adjustments", icon: Clock },
    { title: "Shift Change Request", subtitle: "Move to a different work schedule", to: "/me/time?tab=adjustments", icon: CalendarCheck },
    { title: "HR Assistant", subtitle: "Ask about leave, pay & policies", to: "/ai/assistant", icon: Sparkles },
    { title: "My Payslips", subtitle: "View and download payslips", to: "/me/payslips", icon: Wallet },
  ];

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user?.first_name || user?.name || "Employee"}`}
        subtitle={`${user?.role || "Team Member"} · ${company?.name || "Company"}`}
        actions={<button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={() => nav("/rewards/exchange")} data-testid="exchange-points-btn"><Gift className="size-4" /> Exchange my reward points</button>}
      />
      <AnnouncementBoard />
      <div className="grid gap-4 md:grid-cols-4">
        {stats.map((s) => (
          <Surface key={s.label} className="p-5 cursor-pointer hover:shadow-md transition" onClick={() => nav(s.to)} data-testid={`my-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{s.label}</div>
              <s.icon className="size-4 text-muted-foreground" />
            </div>
            <div className="mt-2 text-2xl font-bold">{s.value}</div>
            <div className="text-sm text-primary">{s.sub} ›</div>
          </Surface>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-4">
        {actions.map((a) => (
          <Surface key={a.title} className="p-5 cursor-pointer hover:shadow-md transition flex items-start gap-3" onClick={() => nav(a.to)}>
            <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><a.icon className="size-5" /></div>
            <div>
              <div className="font-semibold">{a.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{a.subtitle}</div>
            </div>
          </Surface>
        ))}
      </div>
    </div>
  );
}

export default function MyDashboard() {
  const isMobile = useMobileMode();
  return isMobile ? <PrettyMobileDashboard /> : <DesktopDashboard />;
}
